import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

// Brand-Manual (PDF/DOCX) hochladen → KI liest es → Vorschlag je Feld prüfen → bestätigte Felder übernehmen.
// Backend: POST /settings/brand-dna/manual-import, GET .../{job}, POST .../{job}/apply
// Es wird NICHTS geschrieben, bevor der Nutzer Felder bestätigt.

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;
const C = { page: '#0A1628', card: '#1A2B42', border: '#2A3B52', text: '#FFFFFF', accent: '#00D9C8', muted: '#A0AEC0', warn: '#F59E0B' };

const L = {
  de: {
    title: 'Brand Manual hochladen',
    sub: 'Lade dein Brand- oder Corporate-Design-Handbuch (PDF, DOCX, MD oder TXT) hoch. Die KI liest es und schlägt dir die Felder deiner Brand DNA vor – du bestätigst, was übernommen wird.',
    choose: 'Datei wählen (PDF / DOCX / MD / TXT)', reading: 'Dokument wird gelesen…', analyzing: 'KI analysiert das Manual – das kann bis zu 2 Minuten dauern…',
    nothing: 'Im Dokument wurden keine neuen Angaben gefunden, die sich von deiner aktuellen Brand DNA unterscheiden.',
    reviewTitle: 'Vorschlag prüfen', apply: (n) => `${n} Feld${n === 1 ? '' : 'er'} übernehmen`, cancel: 'Verwerfen',
    selectAll: 'Alle', selectNone: 'Keine', current: 'Aktuell', proposed: 'Vorschlag', added: 'Neu hinzu',
    empty: '(leer)', verified: 'Beleg im Dokument gefunden', unverified: 'Beleg nicht eindeutig im Dokument gefunden – bitte prüfen',
    applied: 'Übernommen', failed: 'Fehlgeschlagen', onlyPdf: 'Erlaubt sind PDF, DOCX, MD und TXT.',
    step1: '1 · Was wurde aus der Datei gelesen?', step2: '2 · Was wird in die Brand DNA übernommen?', step3: '3 · Was fließt in deinen Content?',
    readChars: (n) => `${n.toLocaleString('de-DE')} Zeichen Text gelesen`, showText: 'Gelesenen Text anzeigen', hideText: 'Text ausblenden',
    foundFields: 'In der Datei gefunden', notFound: 'Nicht in der Datei gefunden (bleiben unverändert)',
    st_neu: 'neu', st_gleich: 'bereits identisch', st_abweichend: 'weicht ab', st_ergänzt: 'ergänzt',
    target: 'Ziel in der Brand DNA', whereDna: 'Ziel in der Brand DNA', social: 'Kurz-Posts (LinkedIn, Instagram, Facebook, X, Pinterest, Threads, TikTok)', long: 'Lang-Formate (Blog, Newsletter, Pressemitteilung, YouTube)', image: 'Bilder',
    doneTitle: 'Übernommen — so geht es weiter', saved: 'gespeichert & geprüft', notSaved: 'NICHT wie erwartet gespeichert',
    doneNote: 'Die Änderungen gelten ab sofort für neu erstellte Inhalte. Bereits erstellte Beiträge ändern sich nicht.', again: 'Weiteres Dokument hochladen',
    hint: 'Gelesen wird nur markierbarer Text. Gescannte PDFs ohne Text bitte als DOCX oder mit Texterkennung hochladen.',
  },
  en: {
    title: 'Upload brand manual',
    sub: 'Upload your brand or corporate design guide (PDF, DOCX, MD or TXT). The AI reads it and proposes your Brand DNA fields — you confirm what gets applied.',
    choose: 'Choose file (PDF / DOCX / MD / TXT)', reading: 'Reading document…', analyzing: 'AI is analysing the manual — this can take up to 2 minutes…',
    nothing: 'No new details found in the document that differ from your current Brand DNA.',
    reviewTitle: 'Review proposal', apply: (n) => `Apply ${n} field${n === 1 ? '' : 's'}`, cancel: 'Discard',
    selectAll: 'All', selectNone: 'None', current: 'Current', proposed: 'Proposed', added: 'New',
    empty: '(empty)', verified: 'Evidence found in document', unverified: 'Evidence not clearly found in the document — please check',
    applied: 'Applied', failed: 'Failed', onlyPdf: 'Allowed: PDF, DOCX, MD and TXT.',
    step1: '1 · What was read from the file?', step2: '2 · What goes into your Brand DNA?', step3: '3 · What flows into your content?',
    readChars: (n) => `${n.toLocaleString('en-US')} characters of text read`, showText: 'Show text that was read', hideText: 'Hide text',
    foundFields: 'Found in the file', notFound: 'Not found in the file (stay unchanged)',
    st_neu: 'new', st_gleich: 'already identical', st_abweichend: 'differs', st_ergänzt: 'extended',
    target: 'Target in Brand DNA', whereDna: 'Target in Brand DNA', social: 'Short posts (LinkedIn, Instagram, Facebook, X, Pinterest, Threads, TikTok)', long: 'Long-form (blog, newsletter, press release, YouTube)', image: 'Images',
    doneTitle: 'Applied — here is what happens next', saved: 'saved & verified', notSaved: 'NOT saved as expected',
    doneNote: 'Changes apply immediately to newly created content. Existing posts are not changed.', again: 'Upload another document',
    hint: 'Only selectable text is read. For scanned PDFs without text, please upload a DOCX or an OCR’d PDF.',
  },
};

const btn = (primary) => ({
  fontSize: 13, fontWeight: 600, padding: '8px 14px', borderRadius: 8, cursor: 'pointer',
  background: primary ? C.accent : 'transparent', color: primary ? '#06222a' : C.muted,
  border: primary ? 'none' : `1px solid ${C.border}`,
});

const renderItem = (kind, x) => {
  if (typeof x === 'string') return x;
  if (kind === 'colors') return `${x.hex}${x.role ? ` · ${x.role}` : ''}${x.usage ? ` – ${x.usage}` : ''}`;
  if (kind === 'themes') return `${x.title}${x.description ? ` – ${x.description}` : ''}`;
  if (kind === 'ctas') return `${x.cta}${x.context ? ` (${x.context})` : ''}`;
  if (kind === 'personas') return `${x.name}${x.role === 'primary' ? ' (primär)' : ''}${x.pain_point ? ` – ${x.pain_point}` : ''}`;
  return JSON.stringify(x);
};


const stepTitle = { fontSize: 15, fontWeight: 700, margin: '20px 0 8px', color: C.accent };
const badge = (st) => ({
  fontSize: 11, padding: '1px 7px', borderRadius: 999, marginLeft: 8, fontWeight: 600,
  background: st === 'gleich' ? '#2A3B52' : `${C.accent}22`, color: st === 'gleich' ? C.muted : C.accent,
});
const valueText = (kind, v) => (Array.isArray(v) ? v.map((x) => renderItem(kind, x)).join(' · ') : String(v));

const UsageLines = ({ usage, t }) => (
  usage?.section ? (
    <div style={{ fontSize: 12.5, color: C.muted, marginTop: 6 }}>
      <b style={{ color: C.text }}>{t.target}:</b> {usage.section}
    </div>
  ) : null
);

export default function BrandManualImport({ getAuthHeader, lang = 'de', onApplied }) {
  const t = L[lang] || L.de;
  const fileRef = useRef(null);
  const [jobId, setJobId] = useState(null);
  const [readyJob, setReadyJob] = useState(null);
  const [stage, setStage] = useState('idle'); // idle | reading | analyzing | review | done
  const [proposal, setProposal] = useState([]);
  const [report, setReport] = useState(null);
  const [results, setResults] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [selected, setSelected] = useState({});
  const [filename, setFilename] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!jobId) return;
    let stopped = false;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/settings/brand-dna/manual-import/${jobId}`, { headers: getAuthHeader() });
        if (stopped) return;
        const j = r.data;
        if (j.status === 'analyzing') setStage('analyzing');
        if (j.status === 'ready') {
          setProposal(j.proposal || []);
          setReport(j.report || null);
          setSelected(Object.fromEntries((j.proposal || []).map((e) => [e.path, true])));
          setReadyJob(jobId); setStage('review'); setJobId(null);
        } else if (j.status === 'error') {
          toast.error(j.error || t.failed); setStage('idle'); setJobId(null);
        }
      } catch (e) {
        if (!stopped) { toast.error(e?.response?.data?.detail || t.failed); setStage('idle'); setJobId(null); }
      }
    };
    const iv = setInterval(tick, 2500);
    return () => { stopped = true; clearInterval(iv); };
    // eslint-disable-next-line
  }, [jobId]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/\.(pdf|docx|md|markdown|txt)$/i.test(file.name)) { toast.error(t.onlyPdf); return; }
    setFilename(file.name); setStage('reading');
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await axios.post(`${API}/settings/brand-dna/manual-import`, fd,
        { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
      setJobId(r.data.job_id);
    } catch (err) {
      setStage('idle');
      toast.error(err.response?.data?.detail || t.failed);
    }
  };

  const chosen = proposal.filter((p) => selected[p.path]).map((p) => p.path);

  const apply = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/settings/brand-dna/manual-import/${readyJob}/apply`, { paths: chosen }, { headers: getAuthHeader() });
      setResults(r.data.results || []);
      toast.success(t.applied); setStage('done'); setProposal([]);
      onApplied && onApplied();
    } catch (err) {
      toast.error(err.response?.data?.detail || t.failed);
    } finally { setBusy(false); }
  };
  const discard = () => { setStage('idle'); setProposal([]); };

  const box = { background: C.page, border: `1px solid ${C.border}`, borderRadius: 10, padding: '10px 12px', marginTop: 6, fontSize: 13.5, lineHeight: 1.5 };

  return (
    <div>
      <p style={{ fontSize: 13, color: C.muted, margin: '0 0 12px' }}>{t.sub}</p>
      {stage === 'done' && (
        <div style={{ marginBottom: 16 }}>
          <p style={stepTitle}>{t.doneTitle}</p>
          {results.map((r) => (
            <div key={r.path} style={{ borderTop: `1px solid ${C.border}`, padding: '10px 0' }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>
                {r.label} <span style={{ fontSize: 12, fontWeight: 600, color: r.saved ? '#34D399' : C.warn }}>· {r.saved ? `✓ ${t.saved}` : `⚠ ${t.notSaved}`}</span>
              </div>
              <UsageLines usage={r.usage} t={t} />
            </div>
          ))}
          <p style={{ fontSize: 12.5, color: C.muted, marginTop: 8 }}>{t.doneNote}</p>
        </div>
      )}
      {(stage === 'idle' || stage === 'done') && (
        <>
          <input ref={fileRef} type="file" accept=".pdf,.docx,.md,.markdown,.txt" style={{ display: 'none' }} onChange={onFile} />
          <button style={btn(true)} onClick={() => fileRef.current?.click()}>{stage === 'done' ? t.again : t.choose}</button>
          <p style={{ fontSize: 12, color: C.muted, margin: '8px 0 0' }}>{t.hint}</p>
        </>
      )}
      {(stage === 'reading' || stage === 'analyzing') && (
        <p style={{ color: C.accent, fontSize: 14 }}>{filename && `${filename} — `}{stage === 'reading' ? t.reading : t.analyzing}</p>
      )}
      {stage === 'review' && (
        proposal.length === 0 ? (
          <div>
            <p style={{ color: C.muted, fontSize: 14 }}>{t.nothing}</p>
            {report && (
              <div>
                <p style={stepTitle}>{t.step1}</p>
                {(report.found || []).map((f) => (
                  <div key={f.path} style={{ fontSize: 13, padding: '4px 0', borderTop: `1px solid ${C.border}` }}>
                    <b>{f.label}</b><span style={badge(f.status)}>{t['st_' + f.status] || f.status}</span>
                    <div style={{ color: C.muted }}>{valueText(f.kind, f.value)}</div>
                  </div>
                ))}
              </div>
            )}
            <button style={{ ...btn(false), marginTop: 10 }} onClick={discard}>OK</button>
          </div>
        ) : (
          <div>
            {report && (
              <div>
                <p style={stepTitle}>{t.step1}</p>
                <p style={{ fontSize: 13, color: C.muted, margin: '0 0 6px' }}>{filename} — {t.readChars(report.chars || 0)}</p>
                <button style={{ ...btn(false), padding: '3px 8px', fontSize: 12 }} onClick={() => setShowPreview((v) => !v)}>{showPreview ? t.hideText : t.showText}</button>
                {showPreview && <pre style={{ ...box, whiteSpace: 'pre-wrap', fontFamily: 'inherit', maxHeight: 220, overflow: 'auto', color: C.muted, fontSize: 12.5 }}>{report.preview}{report.chars > 1500 ? '\n…' : ''}</pre>}
                <p style={{ fontSize: 13, fontWeight: 600, margin: '14px 0 4px' }}>{t.foundFields}</p>
                {(report.found || []).map((f) => (
                  <div key={f.path} style={{ fontSize: 13, padding: '4px 0', borderTop: `1px solid ${C.border}` }}>
                    <b>{f.label}</b><span style={badge(f.status)}>{t['st_' + f.status] || f.status}</span>
                    <div style={{ color: C.muted }}>{valueText(f.kind, f.value)}</div>
                  </div>
                ))}
                {(report.not_found || []).length > 0 && (
                  <p style={{ fontSize: 12.5, color: C.muted, marginTop: 10 }}><b>{t.notFound}:</b> {report.not_found.join(', ')}</p>
                )}
              </div>
            )}
            <p style={stepTitle}>{t.step2} <span style={{ color: C.muted, fontWeight: 400, fontSize: 12.5 }}>— {t.step3.replace('3 · ', '')}</span></p>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <strong style={{ fontSize: 15 }}>{t.reviewTitle}</strong>
              <span style={{ display: 'flex', gap: 12, fontSize: 12 }}>
                <button style={{ ...btn(false), padding: '3px 8px' }} onClick={() => setSelected(Object.fromEntries(proposal.map((e) => [e.path, true])))}>{t.selectAll}</button>
                <button style={{ ...btn(false), padding: '3px 8px' }} onClick={() => setSelected({})}>{t.selectNone}</button>
              </span>
            </div>
            {proposal.map((e) => (
              <div key={e.path} style={{ borderTop: `1px solid ${C.border}`, marginTop: 14, paddingTop: 12 }}>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
                  <input type="checkbox" checked={!!selected[e.path]} onChange={(ev) => setSelected({ ...selected, [e.path]: ev.target.checked })} />
                  {e.label}
                </label>
                {e.action === 'replace' ? (
                  <>
                    <div style={box}><span style={{ color: C.muted, fontSize: 11 }}>{t.current}</span><br />{e.current || <em style={{ color: C.muted }}>{t.empty}</em>}</div>
                    <div style={{ ...box, borderColor: `${C.accent}66` }}><span style={{ color: C.accent, fontSize: 11 }}>{t.proposed}</span><br />{e.proposed}</div>
                  </>
                ) : (
                  <div style={{ ...box, borderColor: `${C.accent}66` }}>
                    <span style={{ color: C.accent, fontSize: 11 }}>{t.added}</span>
                    <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>{(e.added || []).map((x, i) => <li key={i}>{renderItem(e.kind, x)}</li>)}</ul>
                  </div>
                )}
                <UsageLines usage={e.usage} t={t} />
                {e.evidence && (
                  <p style={{ fontSize: 12, margin: '6px 0 0', color: e.verified ? C.muted : C.warn }}>
                    „{e.evidence}“ — {e.verified ? t.verified : t.unverified}
                  </p>
                )}
              </div>
            ))}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
              <button style={btn(false)} onClick={discard} disabled={busy}>{t.cancel}</button>
              <button style={{ ...btn(true), opacity: chosen.length ? 1 : 0.5 }} disabled={busy || !chosen.length} onClick={apply}>{busy ? '…' : t.apply(chosen.length)}</button>
            </div>
          </div>
        )
      )}
    </div>
  );
}
